<template>
  <main class="notification-center-page">
    <div class="notification-center-page__body">
      <div class="notification-center-page__row">
        <h1 class="notification-center-page__title">Рассылки</h1>
        <div v-if="hasChannels" class="notification-center-page__buttons">
          <button
            class="notification-center-page__buttons-add"
            type="button"
            v-on:click="openPlanForm()">
            Новый план
          </button>
        </div>
      </div>
      <div v-if="hasChannels" class="notification-center-page__table">
        <div class="notification-center-page__table__head">
          <div class="notification-center-page__table__cell left">Название</div>
          <div class="notification-center-page__table__cell left">Событие</div>
          <div class="notification-center-page__table__cell">Последний запуск</div>
          <div class="notification-center-page__table__cell">Канал отправки</div>
          <div class="notification-center-page__table__cell">Активен</div>
          <div class="notification-center-page__table__cell" />
        </div>
        <div
          v-if="!this.planListIsEmpty && this.planListIsFetched"
          class="notification-center-page__table__body"
        >
          <div
            class="notification-center-page__table__row"
            v-bind:key="plan.id"
            v-for="plan in planList">
            <div
              class="notification-center-page__table__cell left"
              data-label="Название">
              <button
                type="button"
                v-on:click="openPlanForm(plan.id)"
                class="notification-center-page__table__cell__name">{{ plan.title }}</button>
            </div>
            <div
              data-label="Событие"
              class="notification-center-page__table__cell left">
              {{ plan.eventName }}
            </div>
            <div
              data-label="Последний запуск"
              class="notification-center-page__table__cell">
              {{ plan.firedAt }}
            </div>
            <div
              data-label="Канал отправки"
              class="notification-center-page__table__cell">
              {{ plan.channelString }}
            </div>
            <div
              data-label="Активен"
              class="notification-center-page__table__cell">
              <v-switch
                class="notification-center-page__table__switch"
                v-model="plan.activity"
                v-on:change="onChangeActivity($event, plan.id)"
              />
            </div>
            <div
              class="notification-center-page__table__cell"
              data-label="Редактировать"
            >
              <button
                class="notification-center-page__table__icon-edit"
                type="button"
                v-on:click="openPlanForm(plan.id)"
                title="Редактировать"
              />
            </div>
            <div
              class="notification-center-page__table__cell"
              data-label="Удалить"
            >
              <button
                class="notification-center-page__table__icon-delete"
                type="button"
                title="Удалить"
                v-on:click.prevent="onDeletePlan(plan.id)" />
            </div>
          </div>
        </div>
        <div
          v-else-if="!isAwaitRequest"
          class="notification-center-page__table__message"
        >
          Ничего не найдено
        </div>
      </div>
      <div
        v-else-if="isOptionsLoaded"
        class="notification-center-page__section-description"
      >
        <p>Для работы модуля подключите SMS или WhatsApp.</p>
        <div>
          <p>С модулем Рассылки вы сможете настроить уникальные сценарии оповещения пациентов:</p>
          <ul>
            <li>напомнить о предстоящем визите,</li>
            <li>поздравить с праздниками,</li>
            <li>информировать об акциях.</li>
          </ul>
          <p>Рассылки можно производить по двум каналам связи: sms или WhatsApp.</p>
        </div>
        <p>
          Подробности у нашего менеджера, Татьяны <a href="tel:+79617210271">+7 (961) 721-02-71</a>
        </p>
      </div>
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-if="shownPlanForm"
        v-bind:label="formTitle"
        v-bind:need-confirm="needConfirmForm"
        v-on:cancel="closeForm">
        <form-notification-plan
          v-bind:id="plan.id"
          v-bind:initial-name="plan.title"
          v-bind:initial-fragments="plan.fragments"
          v-bind:initial-event="plan.event"
          v-bind:initial-channel="plan.channel"
          v-bind:initial-dispatch-time="plan.dispatchTime"
          v-bind:initial-period="plan.periodLastVisit"
          v-bind:initial-sex-id="plan.sexId"
          v-bind:initial-repeatable="plan.repeatable"
          v-bind:initial-service="plan.service"
          v-bind:initial-funnel="plan.funnel"
          v-bind:initial-stage="plan.stage"
          v-bind:count-notifiable-patients="countNotifiablePatients"
          v-bind:description-notifiable-patients="descriptionNotifiablePatients"
          v-bind:events="events"
          v-bind:channels="channels"
          v-bind:service-options="serviceOptions"
          v-bind:funnel-options="funnelOptions"
          v-bind:stage-options="stageOptions"
          v-bind:violations="inputErrors"
          v-on:change-values="onChangeFieldValues"
          v-on:submit="onSubmitPlan"
          v-on:change-options="fetchNotifiablePatients"
        />
      </confirm-side-dialog>
    </side-slide-transition>
  </main>
</template>

<script>
import { NotificationPlan } from '@/storage/gateways/NotificationPlansGateway';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import FormNotificationPlan from '@/components/FormNotificationPlan.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';

/**
 * @description Страница управления рассылками клиники.
 * @returns {Vue}
 */
export default {
  components: {
    FormNotificationPlan,
    SideSlideTransition,
    ConfirmSideDialog,
  },
  computed: {
    /**
     * @description Есть каналы отправки.
     * @returns {boolean}
     */
    hasChannels() {
      return this.channels.length > 0;
    },
    /**
     * @description Заголовок формы.
     * @returns {string}
     */
    formTitle() {
      return this.plan.id !== null
        ? 'Редактировать план рассылки'
        : 'Создать план рассылки';
    },
    /**
     * @description Список планов пустой.
     * @returns {boolean}
     */
    planListIsEmpty() {
      return this.planList.length === 0;
    },
  },
  created() {
    this.fetchOptions().finally(() => {
      this.isOptionsLoaded = true;
      this.fetchPlanList();
    });
  },
  data() {
    return {
      /**
       * @description Список планов.
       * @type {Array}
       */
      planList: [],
      plan: new NotificationPlan(null),
      shownPlanForm: false,
      isAwaitRequest: false,
      isOptionsLoaded: false,
      planListIsFetched: false,
      events: [],
      channels: [],
      serviceOptions: [],
      funnelOptions: [],
      stageOptions: [],
      countNotifiablePatients: 0,
      descriptionNotifiablePatients: '',
      inputErrors: {},
      needConfirmForm: false,
    };
  },
  methods: {
    /**
     * @description Получить опции.
     * @returns {Promise}
     */
    fetchOptions() {
      const onSuccess = (
        {
          services,
          events,
          funnels,
          stages,
          channels,
        },
      ) => {
        this.serviceOptions = services;
        this.events = events;
        this.funnelOptions = funnels;
        this.stageOptions = stages;
        this.channels = channels;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      return this
        .$notificationPlansGateway
        .getOptions()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить число уведомляемых пациентов.
     * @param request
     */
    fetchNotifiablePatients(request) {
      const onSuccess = ({ count, description }) => {
        this.countNotifiablePatients = count;
        this.descriptionNotifiablePatients = description;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$notificationPlansGateway
        .viewNotifiablePatients(request)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить список планов.
     * @returns {Promise<void>}
     */
    async fetchPlanList() {
      const onResponse = (result) => {
        result.forEach(({ plans }) => {
          this.planList = plans;
        });
      };
      this.planListIsFetched = false;
      this
        .$notificationPlansGateway
        .getPlanList()
        .then(onResponse)
        .finally(() => { this.planListIsFetched = true; });
    },
    /**
     * @description Изменить активность плана.
     * @param {boolean} value
     * @param {string}  planId
     */
    onChangeActivity(value, planId) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$notificationPlansGateway
        .changeActivityPlan(planId, value)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать изменения полей.
     * @param {string} form
     * @param {boolean} isChange
     * @returns {void}
     */
    onChangeFieldValues(form, isChange) {
      this.needConfirmForm = isChange;
    },
    /**
     * @description Обработать удаление плана.
     * @param {string} id
     */
    async onDeletePlan(id) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить план уведомлений?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchPlanList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$notificationPlansGateway
        .deletePlan(id)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать открытие формы.
     * @param planId
     */
    openPlanForm(planId = null) {
      if (planId !== null) {
        this.fetchPlan(planId);
      } else {
        this.plan = new NotificationPlan(null);
        this.openForm();
      }
    },
    /**
     * @description Получить план.
     * @param planId
     */
    fetchPlan(planId) {
      const onResponse = (result) => {
        result.forEach(({ plan }) => {
          this.plan = plan;
          this.openForm();
        });
      };
      this
        .$notificationPlansGateway
        .viewPlan(planId)
        .then(onResponse);
    },
    /**
     * @description Закрыть форму плана рассылки.
     * @returns {void}
     */
    closeForm() {
      this.shownPlanForm = false;
    },
    /**
     * @description Открыть форму плана рассылки.
     * @returns {void}
     */
    openForm() {
      this.inputErrors = {};
      this.shownPlanForm = true;
    },
    /**
     * @description Обработать сохранение.
     * @param {NotificationPlanCreationRequest} request
     */
    onSubmitPlan(request) {
      if (request.getId() !== null) {
        this.editPlan(request);
      } else {
        this.createPlan(request);
      }
    },
    /**
     * @description Создать план.
     * @param {NotificationPlanCreationRequest} request
     */
    createPlan(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeForm();
        this.fetchPlanList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$notificationPlansGateway
        .createPlan(request)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Редактировать план.
     * @param {NotificationPlanCreationRequest} request
     */
    editPlan(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeForm();
        this.fetchPlanList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$notificationPlansGateway
        .editPlan(request)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
  ],
  name: 'ClinicPanelNotificationPlans',
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.v-snack__content {
  white-space: pre-line;
}

.notification-center-page {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;

  &__buttons {
    display: flex;
    align-items: center;
    column-gap: 10px;
    max-width: 425px;
    width: 100%;
    &-add {
      @include buttons.btn-blue(100%, 48px, 16px);
      @include buttons.btn-blue-add(19px);
      margin-left: auto;
      max-width: 210px;
    }
    &-delete {
      @include buttons.btn-blue(50%, 48px, 16px)
    }
  }

  &__body {
    padding: 38px 20px 48px 23px;
    width: 100%;
    overflow-y: scroll;
  }

  &__row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 25px;
  }

  &__section-description {
    display: grid;
    gap: 15px;
    ul {
      margin-left: 10px;
      margin-block: 5px;
    }
  }

  &__table {
    counter-reset: counterEmployees;
    overflow-x: auto;
    &__body-mobile {
      display: none;
      flex-direction: column;
      gap: 10px;
      &__row {
        display: grid;
        font-size: 12px;
        grid-template-columns: auto 1fr;
        justify-items: flex-end;
        align-items: center;
        row-gap: 5px;
        padding: 5px;
        &:nth-child(odd) {
          background-color: var(--fill-color);
        }
        & > dt {
          justify-self: flex-start;
        }
        & > dd {
          text-align: right;
        }
      }
    }
    &__head,
    &__row {
      width: 100%;
      text-align: left;
      align-items: center;
      display: grid;
      font-size: 14px;
      grid-template-columns:
          repeat(3, minmax(160px, 1fr))
          minmax(144px, 200px)
          minmax(3rem, .5fr)
          repeat(2, 33px);
      line-height: 17px;
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 10px 0;
    }
    &__row {
      border-bottom: 1px solid #eaeff3;
      padding: 12px 0;
      &:hover {
        background: #eaeff3;
      }
    }
    &__cell {
      text-align: center;
      padding: 0 8px;
      &.left {
        text-align: left;
      }
      &.number:after {
        counter-increment: counterEmployees;
        content: counter(counterEmployees);
      }
      .cabinet-color {
        display: inline-block;
        width: 13px;
        height: 13px;
        border-radius: 50%;
        margin: 0 5px 0 0;
      }
      &__name {
        max-width: 450px;
        text-align: left;
        &:hover {
          cursor: pointer;
          color: var(--action-color);
        }
      }
    }
    &__cell.cda-completeness {
      font-size: 0px;
      &::after {
        font-size: 1rem;
      }
      &.empty::after {
        content: '-';
      }
      &.full::after {
        content: '+';
      }
      &.unknown::after {
        content: '?';
      }
    }
    &__switch {
      .v-input__slot {
        justify-content: center;
      }
    }
    &__icon {
      &-edit {
        display: inline-block;
        height: 14px;
        width: 14px;
        margin: 0;
        padding: 0;
        background: url("@/assets/img/pen-grey.svg") no-repeat 50%;
        background-size: contain;
        cursor: pointer;
        &:hover {
          background-image: url("@/assets/img/pen-blue.svg");
        }
      }
      &-delete {
        width: 14px;
        height: 14px;
        background: url("@/assets/img/exit-grey.svg") no-repeat 50%;
        background-size: contain;
        cursor: pointer;
        z-index: 2;
        &:hover {
          background-image: url("@/assets/img/exit-blue.svg");
        }
      }
    }
    & &__payroll-link::after {
      color: var(--action-color);
      content: '\0020bd';
      display: inline-block;
      font-size: 18px;
      font-weight: 400;
      text-align: center;
      text-decoration: none;
      width: 18px;
    }
    &__message {
      text-align: center;
      padding-top: 40px;
      font-size: 24px;
    }
  }
}

@media screen and (max-width: 960px) {
  .notification-center-page__table {
    &__head,
    &__body {
      min-width: 1100px;
    }
  }
}

@media screen and (max-width: 480px) {
  .notification-center-page {
    &__row {
      flex-direction: column;
      gap: 20px;
    }
    &__buttons-add {
      margin-left: 0;
    }
  }
}
</style>
